import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import AuthModal from './AuthModal';
import HeroAIDemoPanel from './HeroAIDemoPanel';
import HeroLanguagePicker from './HeroLanguagePicker';
import HeroPodcastPanel from './HeroPodcastPanel';
import HeroScreencast from './HeroScreencast';
import HandDrawnUnderline from './ui/HandDrawnUnderline';
import AppStoreBadge from './AppStoreBadge';
import GooglePlayBadge from './GooglePlayBadge';
import { sanitizeInlineHtml } from '../lib/sanitizeHtml';
import { useAuth } from '../contexts/AuthContext';
import { trackPrimaryCtaClicked } from '../services/analyticsService';

const sectionReveal = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.2 },
  transition: { duration: 0.65, ease: [0.22, 1, 0.36, 1] },
};

function normalizeDemoLanguage(language) {
  const normalized = String(language || '').toLowerCase();
  if (normalized.startsWith('es')) return 'es';
  if (normalized.startsWith('no') || normalized.startsWith('nb') || normalized.startsWith('nn')) return 'no';
  return 'en';
}

export default function HeroV2({ episode }) {
  const { t, i18n } = useTranslation('pages');
  const { currentUser, loading: authLoading } = useAuth();
  const prefersReducedMotion = useReducedMotion();
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authSource, setAuthSource] = useState('homepage_demo_completion');
  const demoSectionRef = useRef(null);
  const demoLanguage = normalizeDemoLanguage(episode?.language);
  const screencastCut = String(i18n?.language || '').toLowerCase().startsWith('es') ? 'es' : 'en';

  const subtitleShort = t('hero.subtitleShort', {
    defaultValue: t('hero.subtitle'),
  });

  return (
    <>
      <div className="relative overflow-hidden bg-[#FAF7F2] text-stone-900">
        <section className="relative flex min-h-[100dvh] items-start lg:min-h-[calc(100dvh-72px)] lg:items-center">
          <div className="mx-auto w-full max-w-[1320px] lg:min-[1680px]:max-w-[1640px] px-5 pb-12 pt-10 sm:px-8 sm:pt-14 lg:px-10 lg:py-12">
            <motion.div
              className="mx-auto flex max-w-[1240px] lg:min-[1680px]:max-w-[1560px] flex-col items-center gap-6 text-center lg:grid lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] lg:items-center lg:gap-x-14 lg:gap-y-7 lg:min-[1680px]:gap-x-20 lg:text-left"
              initial={prefersReducedMotion ? false : { opacity: 0, y: 20 }}
              animate={prefersReducedMotion ? false : { opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="contents lg:block lg:w-fit">
                <h1
                  className="order-1 text-stone-950"
                  style={{ fontFamily: 'var(--font-display)', fontWeight: 700 }}
                >
                  <span className="block lg:whitespace-nowrap text-[clamp(2.3rem,10vw,7.8rem)] lg:text-[clamp(2.6rem,4.5vw,4.4rem)] lg:min-[1680px]:text-[clamp(4.4rem,3.9vw,5.5rem)] leading-[1.0] tracking-[-0.04em]">
                    Finally, language
                  </span>
                  <span className="block lg:whitespace-nowrap text-[clamp(2.3rem,10vw,7.8rem)] lg:text-[clamp(2.6rem,4.5vw,4.4rem)] lg:min-[1680px]:text-[clamp(4.4rem,3.9vw,5.5rem)] leading-[1.0] tracking-[-0.04em]">
                    learning that
                  </span>
                  <span className="relative mt-2 inline-block whitespace-nowrap text-[clamp(2.3rem,10vw,7.8rem)] lg:text-[clamp(2.6rem,4.5vw,4.4rem)] lg:min-[1680px]:text-[clamp(4.4rem,3.9vw,5.5rem)] italic leading-[1.0] tracking-[-0.04em] text-[#D63AB4]">
                    <span className="relative z-10">makes sense</span>
                    <HandDrawnUnderline
                      className="absolute inset-x-0 -bottom-3 h-[16px] translate-x-[1%]"
                      delay={0.2}
                    />
                  </span>
                </h1>

                <p
                  className="order-2 mx-auto max-w-[45rem] text-[1.14rem] leading-[1.34] text-[#5f6368] sm:text-[1.24rem] lg:mx-0 lg:mt-9 lg:max-w-[31rem] lg:text-[18px] lg:min-[1680px]:max-w-[36rem] lg:min-[1680px]:text-[20px]"
                  style={{ fontFamily: 'var(--font-body)' }}
                  dangerouslySetInnerHTML={{ __html: sanitizeInlineHtml(subtitleShort) }}
                />

                <p
                  className="hidden max-w-[31rem] lg:min-[1680px]:max-w-[36rem] text-[16px] lg:min-[1680px]:text-[17px] italic leading-[1.5] text-stone-700 lg:mt-5 lg:block"
                  style={{ fontFamily: 'var(--font-body)', fontWeight: 500 }}
                >
                  {t('hero.philosophyLine', 'Because language is not learned one random sentence at a time.')}
                </p>

                <div className="contents lg:mx-auto lg:mt-6 lg:flex lg:w-fit lg:flex-col lg:items-center lg:gap-4">
                  {!authLoading && !currentUser ? (
                    <button
                      type="button"
                      onClick={() => {
                        trackPrimaryCtaClicked({
                          source: 'homepage',
                          ctaLocation: 'hero',
                          landingPage: typeof window !== 'undefined' ? window.location.pathname : '/',
                          language: episode?.language,
                        });
                        demoSectionRef.current?.scrollIntoView({
                          behavior: prefersReducedMotion ? 'auto' : 'smooth',
                          block: 'start',
                        });
                        window.requestAnimationFrame(() => demoSectionRef.current?.focus({ preventScroll: true }));
                      }}
                      className="order-3 inline-flex min-h-[52px] w-auto items-center justify-center rounded-full bg-stone-950 px-9 py-3.5 lg:min-h-[56px] lg:py-4 text-[1.08rem] font-semibold text-white shadow-[0_22px_40px_-26px_rgba(28,25,23,0.75)] transition hover:-translate-y-0.5 hover:bg-stone-800 active:translate-y-0 active:scale-[0.985] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D63AB4] focus-visible:ring-offset-2"
                      style={{ fontFamily: 'var(--font-body)' }}
                    >
                      {t('hero.startFree', 'Start free')}
                    </button>
                  ) : null}
                  <div className="order-5 flex shrink-0 items-center justify-center gap-3 lg:justify-center lg:gap-2">
                    <div className="w-[132px] lg:h-[36px] lg:w-[104px]">
                      <AppStoreBadge size="small" className="lg:origin-top-left lg:scale-[0.79]" />
                    </div>
                    <div className="w-[132px] lg:h-[36px] lg:w-[104px]">
                      <GooglePlayBadge size="small" className="lg:origin-top-left lg:scale-[0.79]" />
                    </div>
                  </div>
                </div>

              </div>

              <div className="order-4 w-full min-w-0 lg:order-none">
                <HeroScreencast
                  cut={screencastCut}
                  showSignup={!authLoading && !currentUser}
                  onSignup={() => {
                    setAuthSource('homepage_screencast');
                    setAuthModalOpen(true);
                  }}
                />
              </div>

              <p
                className="hidden items-center gap-2 whitespace-nowrap text-[13px] leading-[1.5] text-stone-600 lg:col-span-2 lg:flex lg:min-[1680px]:text-[14px]"
                style={{ fontFamily: 'var(--font-body)' }}
              >
                <span className="h-px w-[18px] shrink-0 bg-stone-400" aria-hidden="true" />
                <span>{t('hero.credentials', 'Designed by a PhD linguist after 20 years of teaching at universities around the world.')}</span>
              </p>
            </motion.div>
          </div>
        </section>

        <motion.section
          id="free-demo"
          ref={demoSectionRef}
          tabIndex={-1}
          className="relative scroll-mt-24 bg-[#FAF7F2]"
          {...(prefersReducedMotion ? {} : sectionReveal)}
        >
          <div className="mx-auto max-w-[1160px] px-5 py-16 sm:px-8 lg:px-10 lg:py-20">
            <div className="mb-10 max-w-[34rem]">
              <p className="text-[12px] font-semibold uppercase tracking-[0.18em] text-[#B2288E]">
                {t('hero.demoCards.podcast.badge', 'Podcast')}
              </p>
              <h2
                className="mt-3 text-4xl leading-tight text-stone-950 sm:text-5xl"
                style={{ fontFamily: 'var(--font-display)' }}
              >
                {t('hero.demoCards.podcast.title', 'Try a demo episode')}
              </h2>
              <p className="mt-4 text-[1rem] leading-7 text-stone-600 sm:text-[1.06rem]">
                {episode?.description || t('hero.demoCards.podcast.subtitle')}
              </p>
            </div>

            <HeroPodcastPanel
              episode={episode}
              onRequestSignup={() => {
                setAuthSource('homepage_demo_completion');
                setAuthModalOpen(true);
              }}
            />
          </div>
        </motion.section>

        <motion.section
          className="relative bg-[#FAF7F2]"
          {...(prefersReducedMotion ? {} : sectionReveal)}
        >
          <div className="mx-auto grid max-w-[1160px] gap-10 px-5 py-16 sm:px-8 lg:grid-cols-[minmax(0,0.72fr)_minmax(0,1fr)] lg:items-start lg:gap-12 lg:px-10 lg:py-20">
            <div className="max-w-[30rem]">
              <p className="text-[12px] font-semibold uppercase tracking-[0.18em] text-[#B2288E]">
                {t('hero.demoCards.ai.badge', 'AI Coach')}
              </p>
              <h2
                className="mt-3 text-4xl leading-tight text-stone-950 sm:text-5xl"
                style={{ fontFamily: 'var(--font-display)' }}
              >
                {t('hero.demoCards.ai.panelTitle', 'Say More! demo')}
              </h2>
              <p className="mt-4 text-[1rem] leading-7 text-stone-600 sm:text-[1.06rem]">
                {t('hero.demoCards.ai.subtitle')}
              </p>
            </div>

            <HeroAIDemoPanel />
          </div>
        </motion.section>

        <motion.section
          className="relative bg-[#FAF7F2]"
          {...(prefersReducedMotion ? {} : sectionReveal)}
        >
          <div className="mx-auto max-w-[1160px] px-5 py-16 sm:px-8 lg:px-10 lg:py-20">
            <div className="mx-auto max-w-[760px] text-center">
              <p className="text-[12px] font-semibold uppercase tracking-[0.18em] text-[#B2288E]">
                {t('hero.startLearning')}
              </p>
              <h2
                className="mt-3 text-4xl leading-tight text-stone-950 sm:text-5xl"
                style={{ fontFamily: 'var(--font-display)' }}
              >
                {t('hero.languageSectionTitle', 'Choose your language')}
              </h2>
              <p className="mx-auto mt-4 max-w-[34rem] text-[1rem] leading-7 text-stone-600 sm:text-[1.06rem]">
                {t('hero.description')}
              </p>
            </div>

            <div className="mt-10">
              <HeroLanguagePicker
                showLabel={false}
                className="gap-5"
                listClassName="gap-3"
                pillClassName="min-h-[44px] px-4 py-1.5 text-sm sm:px-5 sm:py-2"
              />
            </div>

            <div className="mt-8 text-center">
              <Link
                to="/choose-language?intent=placement-test"
                className="text-sm font-semibold text-stone-700 transition hover:text-[#B2288E] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D63AB4] focus-visible:ring-offset-2"
                style={{ fontFamily: 'var(--font-body)' }}
              >
                {`${t('hero.getStarted', 'Find your level')} - ${t('hero.placementDuration', '10 min')}`}
              </Link>
            </div>
          </div>
        </motion.section>
      </div>

      <AuthModal
        isOpen={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
        mode="signup"
        analyticsSource={authSource}
        analyticsLanguage={episode?.language}
        signupContext={{
          demoId: episode?.id,
          language: demoLanguage,
          level: episode?.level,
          returnTo: `/${demoLanguage}/onboarding/welcome`,
        }}
      />
    </>
  );
}
